/**
 * @fileoverview Section "Portal" — composants métier du portail citoyen.
 *
 * Ces composants portent les invariants UX du parcours public : aucun champ
 * nominatif, montants XOF entiers, statuts lisibles, preuve disponible dès PAID
 * et renvoi vers le compte Quipux pour la quittance officielle (hors GNPC).
 */

import { useState } from 'react';
import {
  Amount,
  Button,
  ContraventionCard,
  DangerNotice,
  FaqList,
  InfoNotice,
  PaymentProofActions,
  PaymentSummary,
  PlateInput,
  PortalState,
  StatusBadge,
  SuccessBanner,
  WarningNotice,
} from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionPortal(): JSX.Element {
  const [plate, setPlate] = useState('AB572GG');

  return (
    <div className="space-y-8">
      <div className="grid gap-4 lg:grid-cols-2">
        <DsPreview label="PlateInput — valeur canonique AB572GG">
          <div className="w-full space-y-2">
            <PlateInput value={plate} onChange={setPlate} />
            <p className="text-muted-foreground font-mono text-xs">canonical={plate}</p>
          </div>
        </DsPreview>

        <DsPreview label="Amount — XOF entier, jamais de décimales">
          <div className="flex flex-wrap items-center gap-4">
            <Amount value={45000} />
            <Amount value={2_000_000} compact />
          </div>
        </DsPreview>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DsPreview label="StatusBadge — payment_status / sgi_status">
          <div className="flex flex-wrap gap-2">
            <StatusBadge kind="payment" status="PENDING" />
            <StatusBadge kind="payment" status="PAID" />
            <StatusBadge kind="payment" status="FAILED" />
            <StatusBadge kind="sgi" status="RECORDED" />
            <StatusBadge kind="sgi" status="FAILED" />
          </div>
        </DsPreview>

        <DsPreview label="Notices — ton citoyen">
          <div className="grid w-full gap-3">
            <SuccessBanner title="Paiement enregistré">
              Votre justificatif de débit est disponible maintenant.
            </SuccessBanner>
            <InfoNotice title="Quittance officielle délivrée par Quipux">
              Connectez-vous à votre compte Quipux pour la télécharger.
            </InfoNotice>
            <WarningNotice title="Service STI momentanément indisponible">
              Merci de réessayer dans quelques instants.
            </WarningNotice>
            <DangerNotice title="Paiement refusé">
              Aucun débit n'a été effectué. Vous pouvez réessayer ou choisir une autre méthode de
              paiement.
            </DangerNotice>
          </div>
        </DsPreview>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <DsPreview label="ContraventionCard — sans donnée nominative">
          <ContraventionCard
            liquidationNumber="LIQ-921555"
            infractionType="Excès de vitesse"
            amount={45000}
            infractionDate="2026-07-08T08:20:00Z"
            dueDate="2026-08-08T23:59:00Z"
            selected
            action={
              <Button type="button" variant="outline" className="w-full">
                Retirer de la sélection
              </Button>
            }
          />
        </DsPreview>

        <DsPreview label="PaymentSummary — limites de paiement">
          <PaymentSummary selectedCount={3} totalAmount={117000} />
        </DsPreview>
      </div>

      <DsPreview label="PaymentProofActions — justificatif immédiat, quittance via le compte Quipux">
        <div className="w-full">
          <PaymentProofActions debitNoteAvailable />
        </div>
      </DsPreview>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          PortalState — états de parcours citoyen
        </h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <DsPreview label="empty — recherche sans contravention">
            <PortalState
              status="empty"
              title="Aucune contravention trouvée"
              action={
                <Button type="button" variant="outline" size="sm">
                  Modifier la plaque
                </Button>
              }
            >
              Une plaque sans contravention n'est pas une erreur. Vérifiez la saisie ou réessayez
              plus tard.
            </PortalState>
          </DsPreview>

          <DsPreview label="pending — retour guichet / polling">
            <PortalState
              status="pending"
              title="Vérification du paiement"
              progressValue={68}
              badge={<StatusBadge kind="payment" status="PENDING" />}
            >
              Nous confirmons le statut auprès du guichet. Vous pouvez garder cette page ouverte.
            </PortalState>
          </DsPreview>

          <DsPreview label="success — paiement confirmé">
            <PortalState
              status="success"
              title="Paiement enregistré"
              badge={<StatusBadge kind="payment" status="PAID" />}
              action={
                <Button type="button" size="sm">
                  Voir le justificatif
                </Button>
              }
            >
              Le justificatif de débit est disponible. La quittance officielle sera à télécharger
              depuis votre compte Quipux après l'enregistrement STI.
            </PortalState>
          </DsPreview>

          <DsPreview label="error — service indisponible">
            <PortalState
              status="error"
              title="Service STI momentanément indisponible"
              action={
                <Button type="button" variant="outline" size="sm">
                  Réessayer
                </Button>
              }
            >
              Le portail ne peut pas consulter les contraventions pour le moment. Aucun débit n'a
              été lancé.
            </PortalState>
          </DsPreview>

          <DsPreview label="expired — session expirée">
            <PortalState
              status="expired"
              title="Session de paiement expirée"
              badge={<StatusBadge kind="payment" status="EXPIRED" />}
              action={
                <Button type="button" variant="outline" size="sm">
                  Reprendre la sélection
                </Button>
              }
            >
              La fenêtre de paiement n'est plus active. Relancez le paiement depuis votre sélection.
            </PortalState>
          </DsPreview>

          <DsPreview label="failed — paiement refusé">
            <PortalState
              status="failed"
              title="Paiement refusé"
              badge={<StatusBadge kind="payment" status="FAILED" />}
              action={
                <Button type="button" variant="outline" size="sm">
                  Réessayer le paiement
                </Button>
              }
            >
              Aucun règlement n'a été confirmé. Vous pouvez revenir au guichet ou choisir une autre
              méthode de paiement.
            </PortalState>
          </DsPreview>
        </div>
      </div>

      <DsPreview label="FaqList — aide citoyenne">
        <div className="w-full">
          <FaqList
            items={[
              {
                question: 'Quand la quittance officielle est-elle disponible ?',
                answer:
                  "Elle devient disponible après l'enregistrement STI. Le justificatif de débit reste la preuve immédiate.",
                defaultOpen: true,
              },
              {
                question: 'Une recherche vide signifie-t-elle une erreur ?',
                answer:
                  "Non. Une plaque sans contravention est un état normal. L'écran doit rester rassurant.",
              },
            ]}
          />
        </div>
      </DsPreview>
    </div>
  );
}
